import {
  DeleteForeverOutlined,
  EditOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  TextField,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { FormikHelpers, useFormik } from 'formik';
import React, { CSSProperties, useMemo, useState } from 'react';
import { parse } from 'date-fns';
import * as Yup from 'yup';
import { useBlockedTimeStyles } from './styles';

import { IBlockTime } from '@homeporter/types';
import { COLORS } from '../../../../utils/colors';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const BlockedTime = ({
  setShowSave,
  blockTimes,
  setBlockTimes,
}: {
  setShowSave: (show: boolean) => void;
  blockTimes: IBlockTime[];
  setBlockTimes: (blockTimes: IBlockTime[]) => void;
}) => {
  const showSave = false;
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const styles = useBlockedTimeStyles();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const initialValues = useMemo(
    () => ({
      reason: '',
      fromDate: '',
      fromTime: '',
      allDay: true,
      toDate: '',
      toTime: '',
    }),
    [],
  );

  const validationSchema = Yup.object({
    reason: Yup.string().required('Reason is required'),
    fromDate: Yup.string().required('Start date is required'),
    fromTime: Yup.string(),
    allDay: Yup.boolean(),
    toDate: Yup.string(),
    toTime: Yup.string(),
  });

  const formik = useFormik<IBlockTime>({
    initialValues,
    validationSchema,
    onSubmit: async (
      values: IBlockTime,
      { resetForm }: FormikHelpers<IBlockTime>,
    ) => {
      try {
        if (
          (!values.fromDate || values.allDay === true) &&
          (!values.toDate || values.allDay === true) &&
          !values.reason
        ) {
          console.error('Invalid form data');
          return;
        }

        console.log('::::::values:', values, blockTimes);
        /*
        allDay: false
        fromDate: "2025-04-13"
        fromTime: Thu Apr 10 2025 03:00:00 GMT-0400 (Eastern Daylight Time)
        reason: "Vacation"
        toDate: "2025-04-13": "2025-04-13"
        toTime: Thu Apr 10 2025 04:00:00 GMT-0400 (Eastern Daylight Time)
        */

        setShowSave(true);
        if (editingIndex !== null) {
          const updatedBlockTimes = blockTimes.map((item, index) =>
            index === editingIndex ? { ...values } : item,
          );
          setBlockTimes(updatedBlockTimes);
        } else {
          setBlockTimes([...blockTimes, { ...values }]);
        }
        setEditingIndex(null);
        resetForm();
      } catch (err) {
        console.error('Failed to submit form', err);
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const {
    values,
    handleChange,
    touched,
    errors,
    handleSubmit: handleAddBlockTime,
    setFieldValue,
  } = formik;

  const editRow = (index: number) => {
    const selectedRow = blockTimes[index];
    if (selectedRow) {
      formik.setValues(selectedRow);
      setEditingIndex(index);
    }
  };

  const deleteRow = (index: number) => {
    setShowSave(true);
    setBlockTimes(blockTimes.filter((_, i) => i !== index));
  };

  return (
    <>
      <Box margin={'2rem'}>
        <Typography marginTop={'1.5rem'}>
          Block off any time that you will be unavailable without changing your
          overall availability hours.
        </Typography>
        <Grid
          container
          sx={{
            display: 'flex',
            marginTop: '1rem',
            alignItems: 'center',
            columnSpacing: '2rem',
            rowSpacing: '1rem',
            padding: '0',
            '& .MuiGrid-item': {
              padding: '.5rem .5rem',
            },
          }}>
          <Grid item xs={9}>
            <TextField
              fullWidth
              label="Block Reason...."
              name="reason"
              value={values.reason}
              onChange={handleChange}
              error={!!errors.reason}
              helperText={errors.reason}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              sx={styles.addTimeBlockButton}
              variant="outlined"
              onClick={() => handleAddBlockTime()}
              disabled={
                (!values.fromDate && !values.allDay) ||
                ((!values.toDate || !values.toTime || !values.fromTime) &&
                  !values.allDay) ||
                !values.reason
              }>
              {editingIndex !== null ? 'Update' : '+ Add Block Time'}
            </Button>
          </Grid>
          <Grid item xs={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Starting"
                value={
                  values.fromDate
                    ? parse(values.fromDate, 'yyyy-MM-dd', new Date())
                    : null
                }
                onChange={(newValue) =>
                  setFieldValue(
                    'fromDate',
                    newValue ? format(newValue, 'yyyy-MM-dd') : '',
                  )
                }
                slotProps={{
                  textField: {
                    name: 'fromDate',
                    error: !!touched.fromDate && !!errors.fromDate,
                    helperText: touched.fromDate && errors.fromDate,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={4}>
            {values.allDay === false && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="From"
                  ampm={false}
                  minutesStep={5}
                  value={
                    values.fromTime
                      ? parse(values.fromTime, 'HH:mm', new Date())
                      : null
                  }
                  onChange={(newValue) => {
                    setFieldValue(
                      'fromTime',
                      newValue ? format(newValue, 'HH:mm') : '',
                    );
                  }}
                  slotProps={{
                    textField: {
                      name: 'fromTime',
                      error: !!touched.fromTime && !!errors.fromTime,
                      helperText: touched.fromTime && errors.fromTime,
                    },
                  }}
                  format="HH:mm"
                />
              </LocalizationProvider>
            )}
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Switch
                  sx={{ fontSize: '13px' }}
                  checked={values.allDay}
                  onChange={(e) => setFieldValue('allDay', e.target.checked)}
                />
              }
              label="All Day"
            />
          </Grid>
          {values.allDay !== true && (
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Ending"
                  value={
                    values.toDate
                      ? parse(values.toDate, 'yyyy-MM-dd', new Date())
                      : null
                  }
                  onChange={(newValue) =>
                    setFieldValue(
                      'toDate',
                      newValue ? format(newValue, 'yyyy-MM-dd') : '',
                    )
                  }
                  slotProps={{
                    textField: {
                      name: 'toDate',
                      error: !!touched.toDate && !!errors.toDate,
                      helperText: touched.toDate && errors.toDate,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}
          {values.allDay !== true && (
            <Grid item xs={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="To"
                  ampm={false}
                  minutesStep={5}
                  value={
                    values.toTime
                      ? parse(values.toTime, 'HH:mm', new Date())
                      : null
                  }
                  onChange={(newValue) => {
                    setFieldValue(
                      'toTime',
                      newValue ? format(newValue, 'HH:mm') : '',
                    );
                  }}
                  slotProps={{
                    textField: {
                      name: 'toTime',
                      error: !!touched.toTime && !!errors.toTime,
                      helperText: touched.toTime && errors.toTime,
                    },
                  }}
                  format="HH:mm"
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </Box>

      {blockTimes.length > 0 ? (
        <>
          <span style={styles.topBar}></span>
          <Box overflow={'auto'}>
            <table style={styles.tableStyles as CSSProperties}>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Block Reason</th>
                  <th style={{ width: '15%' }}>Starting</th>
                  <th style={{ width: '15%' }}>From</th>
                  <th style={{ width: '15%' }}>Ending</th>
                  <th style={{ width: '15%' }}>To</th>
                  <th style={{ width: '15%' }}></th>
                </tr>
              </thead>
              <tbody>
                {blockTimes.map((item, index) => (
                  <tr
                    key={index}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      ...(styles.tableRowStyles as CSSProperties),
                      backgroundColor:
                        hoveredRow === index ? COLORS.LIGHT_GRAY : 'inherit',
                    }}>
                    <td style={styles.firstCell as CSSProperties}>
                      {item.reason}
                    </td>
                    <td>
                      {typeof item.fromDate === 'string'
                        ? item.fromDate
                        : format(item.fromDate, 'yyyy-MM-dd')}
                    </td>
                    <td>{item.allDay === true ? 'All Day' : item.fromTime}</td>
                    <td>{item.toDate || '-'}</td>
                    <td>{item.toTime || '-'}</td>
                    <td>
                      <EditOutlined
                        sx={styles.icon}
                        onClick={() => {
                          editRow(index);
                        }}
                      />
                      <DeleteForeverOutlined
                        sx={styles.icon}
                        onClick={() => {
                          deleteRow(index);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      ) : null}
    </>
  );
};

export default BlockedTime;
